import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

export const MAX_TOY_TAG_LENGTH = 60;
export const MAX_TOY_TAGS = 48;
export const MAX_TOY_NOTES_LENGTH = 2000;

export function normalizeToyFields(c = {}) {
  const hasArray = Array.isArray(c.toy_items);
  const legacy = c.legacy_gift_request
    || (!hasArray && c.gift_request ? c.gift_request : "");
  return {
    toy_items: hasArray ? c.toy_items : [],
    toy_notes: c.toy_notes || "",
    legacy_gift_request: legacy || "",
  };
}

export function toyPayload(m) {
  const out = {
    toy_items: Array.isArray(m.toy_items) ? m.toy_items : [],
  };
  if (Object.prototype.hasOwnProperty.call(m, "legacy_gift_request")) {
    out.legacy_gift_request = m.legacy_gift_request || "";
  }
  return out;
}

export function validateToyTag(text, items) {
  const t = (text || "").trim().replace(/\s+/g, " ");
  if (!t) return "";
  if (t.length > MAX_TOY_TAG_LENGTH) return `Each item can be at most ${MAX_TOY_TAG_LENGTH} characters.`;
  if (items.some((x) => x.trim().replace(/\s+/g, " ").toLowerCase() === t.toLowerCase())) return `"${t}" is already added.`;
  if (items.length >= MAX_TOY_TAGS) return `You can choose at most ${MAX_TOY_TAGS} items.`;
  return "";
}

export default function ToyRequestField({
  value = [], onChange, notes = "", suggestions = [],
  suggestionsLoading = false, suggestionsError = false, onRetrySuggestions,
  testIdPrefix = "toy", label = "Toy requests", audience = "staff", onPendingTextChange,
}) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState("");
  const wrap = useRef(null);
  const pendingRef = useRef(onPendingTextChange);
  pendingRef.current = onPendingTextChange;
  useEffect(() => { pendingRef.current?.(text); }, [text]);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    return suggestions
      .filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))
      .filter((s) => !q || s.toLowerCase().includes(q))
      .slice(0, 30);
  }, [suggestions, value, text]);

  useEffect(() => {
    function away(e) { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  function add(raw) {
    const t = (raw || "").trim().replace(/\s+/g, " ");
    if (!t) return;
    const err = validateToyTag(t, value);
    if (err) { setError(err); return; }
    onChange([...value, t]);
    setText(""); setError(""); setActive(-1);
  }

  function onKeyDown(e) {
    if (e.nativeEvent?.isComposing) return;
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && filtered[active]) add(filtered[active]);
      else add(text);
    } else if (e.key === "Escape") {
      if (open) e.stopPropagation();
      setOpen(false); setActive(-1);
    }
  }

  const border = { borderColor: "var(--fb-border)" };
  const muted = { color: "var(--fb-text-muted)" };
  const tooLong = text.trim().length > MAX_TOY_TAG_LENGTH;
  const shownOpen = open && filtered.length > 0;

  return (
    <div className="space-y-2" ref={wrap} data-testid={`${testIdPrefix}-field`}>
      <label htmlFor={`${uid}-input`} className="block text-xs font-semibold">{label}</label>
      {suggestionsLoading && <p className="text-xs" role="status">Loading item suggestions…</p>}
      {suggestionsError && (
        <p className="text-xs" role="alert">
          Suggestions couldn’t be loaded. You can still add your own items.{" "}
          <button type="button" onClick={onRetrySuggestions} className="underline" data-testid={`${testIdPrefix}-retry`}>Retry</button>
        </p>
      )}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Selected toy items">
          {value.map((item) => (
            <li key={item} className="inline-flex max-w-full items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium text-white" style={{ background: "var(--fb-primary)" }}>
              <span className="min-w-0" style={{ overflowWrap: "anywhere" }}>{item}</span>
              <button type="button" aria-label={`Remove ${item}`} data-testid={`${testIdPrefix}-remove-${item}`}
                      onClick={() => { onChange(value.filter((v) => v !== item)); setError(""); }}
                      className="shrink-0 p-0.5 rounded-full hover:bg-white/20">
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <input
          id={`${uid}-input`} type="text" role="combobox" autoComplete="off"
          aria-expanded={shownOpen} aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${uid}-opt-${active}` : undefined}
          aria-describedby={`${uid}-help`} aria-invalid={!!error || tooLong}
          placeholder="Search or type a toy, then press Enter"
          value={text} data-testid={`${testIdPrefix}-input`}
          onChange={(e) => { setText(e.target.value); setOpen(true); setActive(-1); setError(""); }}
          onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
          className="w-full px-3 py-2 rounded-lg border" style={border}
        />
        {shownOpen && (
          <ul id={listId} role="listbox" aria-label="Toy suggestions"
              className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white rounded-lg border shadow-lg" style={border}>
            {filtered.map((s, idx) => (
              <li key={s} id={`${uid}-opt-${idx}`} role="option" aria-selected={idx === active}
                  data-testid={`${testIdPrefix}-option-${s}`}
                  onMouseDown={(e) => { e.preventDefault(); add(s); }}
                  className="px-3 py-1.5 text-sm cursor-pointer"
                  style={{ background: idx === active ? "var(--fb-bg)" : "white" }}>
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
      {text.trim() && (
        <button type="button" data-testid={`${testIdPrefix}-add-custom`}
                onClick={() => add(text)} className="text-sm underline font-medium break-all text-left"
                style={{ color: "var(--fb-primary)" }}>
          Add “{text.trim()}”
        </button>
      )}
      <p id={`${uid}-help`} className="text-xs" style={muted}>
        Search for a toy and select a suggestion, or type a new item and click Add or press Enter. Spaces and commas are allowed within an item.
      </p>
      {(error || tooLong) && (
        <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }} data-testid={`${testIdPrefix}-error`}>
          {error || `Each item can be at most ${MAX_TOY_TAG_LENGTH} characters (${text.trim().length}).`}
        </p>
      )}
      {audience === "staff" && notes && (
        <p className="text-xs whitespace-pre-wrap break-words" style={muted} data-testid={`${testIdPrefix}-legacy-notes`}>
          <strong>Previous member notes (staff only, not printed):</strong> {notes}
        </p>
      )}
    </div>
  );
}
